import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, ShieldCheck, User, Mail, Phone, MapPin, BadgeCheck, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BUYER_BADGES } from "../lib/badges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  introducer: "Introducer",
  custodian: "Custodian",
  guarantor: "Guarantor",
};

/**
 * Read-only sponsor card for dependent Super-Stockists.  Renders a **strict
 * whitelist** returned by `/api/my-sponsor`:
 *
 *   - Business name / contact person / email / phone / state
 *   - Verification status pill
 *   - Sponsor vendor code + category
 *
 * By design, this card does NOT display the sponsor's GSTIN, PAN, address,
 * bank details or capital sub-account — a dependent SS must never be able to
 * see their Custodian's classified information.  The backend enforces the
 * same guarantee via a whitelist projection.
 */
export const MyCustodianCard = () => {
  const { user, refresh } = useAuth();
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.buyer_category !== "super_stockist" && user?.buyer_category !== "wholesaler"
        && user?.buyer_category !== "dealer" && user?.buyer_category !== "booking_agent") {
      setLoading(false);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        // Also refresh the top-level user state so sub-role badges elsewhere
        // (Account header, order state machine) stay in sync after an Admin
        // link/unlink action.
        if (refresh) await refresh();
      } catch { /* no-op */ }
      try {
        const r = await axios.get(`${API}/my-sponsor`);
        if (alive) setSponsor(r.data || null);
      } catch { if (alive) setSponsor(null); }
      finally { if (alive) setLoading(false); }
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (loading || !sponsor) return null;

  const cat = sponsor.category || "custodian";
  const badge = BUYER_BADGES[cat];
  const title = `My ${CATEGORY_LABEL[cat] || "Sponsor"}`;
  const isVerified = sponsor.kyc_status === "verified" || sponsor.verified;

  return (
    <div data-testid="my-custodian-card" className="mt-16">
      <h2 className="font-serif text-2xl md:text-3xl mb-4 flex items-center gap-3">
        <ShieldCheck size={20} className="text-[#A78BFA]" />
        {title}
      </h2>
      <p className="font-body text-xs text-[#8A8A82] mb-5 max-w-2xl">
        You're linked to this {CATEGORY_LABEL[cat]?.toLowerCase() || "sponsor"} — your orders route through them for approval before Vinci Admin. Reach out through the contact channels below.
      </p>

      <div className="border border-white/10 rounded-sm bg-gradient-to-br from-[#0a0912] via-[#0a0a0d] to-[#08080b] p-6 md:p-7 relative overflow-hidden"
           style={{ boxShadow: "inset 0 0 60px rgba(167,139,250,0.05)" }}>
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#A78BFA] to-transparent opacity-50" />

        <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-start">
          {/* Category emblem */}
          <span
            data-testid="my-custodian-emblem"
            className="grid place-items-center w-14 h-14 rounded-sm text-black shrink-0"
            style={{ background: `linear-gradient(135deg, ${badge?.from || "#A78BFA"}, ${badge?.to || "#6b4bd9"})` }}
          >
            {badge?.Icon ? <badge.Icon size={22} /> : <Shield size={22} />}
          </span>

          {/* Identity */}
          <div className="min-w-0">
            <div className="font-serif text-2xl text-[#F5F5F0] truncate" data-testid="my-custodian-business">
              {sponsor.business_name || "—"}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#A78BFA] px-2 py-0.5 rounded-sm border border-[#A78BFA]/45 bg-[#A78BFA]/[0.05]">
                {CATEGORY_LABEL[cat] || cat}
              </span>
              {sponsor.vendor_code && (
                <span
                  data-testid="my-custodian-vendor-code"
                  className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#B87333] px-2 py-0.5 rounded-sm border border-[#B87333]/45 bg-[#B87333]/[0.05]"
                >
                  {sponsor.vendor_code}
                </span>
              )}
              {sponsor.state && (
                <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] flex items-center gap-1">
                  <MapPin size={10} /> {sponsor.state}
                </span>
              )}
            </div>

            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 font-body text-sm text-[#C9C9C2]">
              <li className="flex items-center gap-2 min-w-0">
                <User size={13} className="text-[#8A8A82] shrink-0" />
                <span data-testid="my-custodian-contact-person" className="truncate">{sponsor.contact_person || "—"}</span>
              </li>
              <li className="flex items-center gap-2 min-w-0">
                <Mail size={13} className="text-[#8A8A82] shrink-0" />
                {sponsor.email ? (
                  <a data-testid="my-custodian-email" href={`mailto:${sponsor.email}`} className="truncate hover:text-[#B87333] transition-colors">
                    {sponsor.email}
                  </a>
                ) : <span className="text-[#8A8A82]">—</span>}
              </li>
              <li className="flex items-center gap-2 min-w-0">
                <Phone size={13} className="text-[#8A8A82] shrink-0" />
                {sponsor.phone ? (
                  <a data-testid="my-custodian-phone" href={`tel:${sponsor.phone}`} className="truncate hover:text-[#B87333] transition-colors">
                    {sponsor.phone}
                  </a>
                ) : <span className="text-[#8A8A82]">—</span>}
              </li>
            </ul>
          </div>

          {/* Verification pill */}
          <div className="shrink-0">
            {isVerified ? (
              <span data-testid="my-custodian-status-verified"
                    className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#2FB999] px-3 py-1.5 rounded-sm border border-[#2FB999]/50 bg-[#2FB999]/[0.06]">
                <BadgeCheck size={12} /> Verified
              </span>
            ) : (
              <span data-testid="my-custodian-status-pending"
                    className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333] px-3 py-1.5 rounded-sm border border-[#B87333]/50 bg-[#B87333]/[0.06]">
                <Clock size={12} /> Verification pending
              </span>
            )}
          </div>
        </div>

        {/* Explicit privacy footer — reassures the SS *and* documents the intent */}
        <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-start gap-2 font-body text-[11px] text-[#8A8A82]">
          <Shield size={12} className="text-[#8A8A82] shrink-0 mt-0.5" />
          <span>
            Only your {CATEGORY_LABEL[cat]?.toLowerCase() || "sponsor"}'s contact channels are shared with you. Classified information — GSTIN, PAN, banking, address and settlement sub-account — stays private to Vinci and the sponsor.
          </span>
        </div>
      </div>
    </div>
  );
};
